// src/pages/Rejoindre.js
import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../services/firebase";
import { doc, getDoc } from "firebase/firestore";
import { accepterInvitation } from "../services/brevo";
import BoutonGoogle, { Separateur } from "../components/BoutonGoogle";

export default function Rejoindre() {
  const { orgId, tombolaId, invitId } = useParams();
  const { currentUser, inscrireSansOrg, connecter } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("connexion");
  const [form, setForm] = useState({ email: "", password: "", pseudo: "" });
  const [submitting, setSubmitting] = useState(false);
  const [erreurLien, setErreurLien] = useState("");
  const googleLance = useRef(false);

  useEffect(() => {
    async function charger() {
      try {
        const snap = await getDoc(
          doc(db, "organisations", orgId, "tombolas", tombolaId, "invitations", invitId)
        );
        if (!snap.exists() || snap.data().status !== "pending") {
          setErreurLien("Ce lien d'invitation est invalide ou a déjà été utilisé.");
        } else {
          setInvitation({ id: snap.id, ...snap.data() });
          if (snap.data().email) setForm((f) => ({ ...f, email: snap.data().email }));
        }
      } catch {
        setErreurLien("Erreur lors du chargement de l'invitation.");
      }
      setLoading(false);
    }
    charger();
  }, [orgId, tombolaId, invitId]);

  async function rejoindre(user, pseudo) {
    try {
      await user.getIdToken(true);

      // Le serveur vérifie l'invitation, crée l'appartenance, marque l'invitation
      // comme utilisée et envoie l'email de bienvenue.
      await accepterInvitation({ orgId, tombolaId, invitId, pseudo });

      // Fil d'Ariane : on sauvegarde l'ID de la tombola avant de perdre l'URL
      localStorage.setItem(`tl_activeTombola_${user.uid}`, tombolaId);

      window.location.replace("/");
    } catch (err) {
      console.error("rejoindre error:", err);
      setError(err.message || "Impossible de rejoindre la tombola. Réessayez.");
    }
  }

  // Prénom tiré du compte Google (« Marie Dupont » → « Marie »)
  function prenomGoogle(user) {
    return (user?.displayName || "").trim().split(/\s+/)[0] || "";
  }

  async function rejoindreAvecGoogle(user) {
    if (googleLance.current) return; // popup et retour de connexion peuvent arriver ensemble
    googleLance.current = true;
    setSubmitting(true);
    await rejoindre(user, prenomGoogle(user));
    googleLance.current = false;
    setSubmitting(false);
  }

  // Retour de Google par redirection (app installée) : on termine l'invitation
  useEffect(() => {
    let attendu = "";
    try { attendu = sessionStorage.getItem("tl_invitGoogle") || ""; } catch { /* rien */ }
    if (currentUser && invitation && attendu === invitId) {
      try { sessionStorage.removeItem("tl_invitGoogle"); } catch { /* rien */ }
      rejoindreAvecGoogle(currentUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, invitation, invitId]);

  async function handleConnexion(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      // connecter() renvoie directement l'utilisateur (pas { user })
      const user = await connecter(form.email, form.password);
      await rejoindre(user, "");
    } catch (err) {
      if (err.code === "auth/invalid-credential") setError("Email ou mot de passe incorrect.");
      else if (!err.code?.includes("permission")) setError("Erreur. Réessayez.");
    }
    setSubmitting(false);
  }

  async function handleInscription(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const user = await inscrireSansOrg({
        email: form.email,
        password: form.password,
        pseudo: form.pseudo,
      });
      await rejoindre(user, form.pseudo);
    } catch (err) {
      if (err.code === "auth/email-already-in-use") setError("Email déjà utilisé. Connectez-vous.");
      else setError("Erreur. Réessayez.");
    }
    setSubmitting(false);
  }

  if (loading) return <div style={s.center}>⏳ Chargement…</div>;
  if (erreurLien) return (
    <div style={s.wrapper}>
      <div style={s.card}>
        <div style={s.errorIcon}>❌</div>
        <h2 style={s.title}>Lien invalide</h2>
        <p style={s.desc}>{erreurLien}</p>
        <Link to="/" style={s.btn}>Retour à l'accueil</Link>
      </div>
    </div>
  );

  return (
    <div style={s.wrapper}>
      <div style={s.card}>
        <div style={s.header}>
          <span style={s.logo}>🎟️</span>
          <h1 style={s.appName}>TomboLive</h1>
        </div>

        <div style={s.inviteBox}>
          <div style={s.inviteIcon}>{invitation.role === "admin" ? "👑" : "🤝"}</div>
          <p style={s.inviteText}>
            Vous êtes invité(e) à rejoindre la tombola{" "}
            <strong>"{invitation.tombolaNom || "Tombola"}"</strong>{" "}
            en tant que{" "}
            <strong>{invitation.role === "admin" ? "Admin" : "Bénévole"}</strong>.
          </p>
        </div>

        <div style={s.tabs}>
          <button onClick={() => setMode("connexion")}
            style={{ ...s.tab, ...(mode === "connexion" ? s.tabActive : {}) }}>
            J'ai un compte
          </button>
          <button onClick={() => setMode("inscription")}
            style={{ ...s.tab, ...(mode === "inscription" ? s.tabActive : {}) }}>
            Nouveau compte
          </button>
        </div>

        {error && <div style={s.errBox}>⚠️ {error}</div>}

        <div onClickCapture={() => { try { sessionStorage.setItem("tl_invitGoogle", invitId); } catch { /* rien */ } }}>
          <BoutonGoogle
            label="Rejoindre avec Google"
            disabled={submitting}
            onError={setError}
            onUser={async (user) => {
              try { sessionStorage.removeItem("tl_invitGoogle"); } catch { /* rien */ }
              await rejoindreAvecGoogle(user);
            }}
          />
        </div>
        <Separateur texte="ou avec un email et un mot de passe" />

        {mode === "connexion" ? (
          <form onSubmit={handleConnexion} style={s.form}>
            <input type="email" placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required style={s.input} />
            <input type="password" placeholder="Mot de passe" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required style={s.input} />
            <button type="submit" disabled={submitting} style={s.btnPrimary}>
              {submitting ? "…" : "🚀 Rejoindre la tombola"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleInscription} style={s.form}>
            <input type="text" placeholder="Votre prénom ou pseudo"
              value={form.pseudo} onChange={(e) => setForm({ ...form, pseudo: e.target.value })}
              style={s.input} />
            <input type="email" placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required style={s.input} />
            <input type="password" placeholder="Mot de passe (8 min)"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              required style={s.input} />
            <button type="submit" disabled={submitting} style={s.btnPrimary}>
              {submitting ? "…" : "🚀 Créer mon compte et rejoindre"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const s = {
  center: { minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", color:"#667eea" },
  wrapper: { minHeight:"100vh", background:"linear-gradient(135deg,#1a3a8f,#764ba2)", display:"flex", alignItems:"center", justifyContent:"center", padding:"2rem 1rem" },
  card: { background:"#fff", borderRadius:"20px", width:"100%", maxWidth:"420px", padding:"2rem", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" },
  header: { textAlign:"center", marginBottom:"1.5rem" },
  logo: { fontSize:"2rem" },
  appName: { fontSize:"1.3rem", fontWeight:"800", color:"#1a3a8f", margin:"0.25rem 0 0" },
  inviteBox: { background:"#eef0ff", borderRadius:"12px", padding:"1rem", textAlign:"center", marginBottom:"1.5rem", border:"1px solid #c7d2ff" },
  inviteIcon: { fontSize:"2rem", marginBottom:"6px" },
  inviteText: { fontSize:"0.9rem", color:"#333", lineHeight:1.6 },
  tabs: { display:"flex", gap:"6px", marginBottom:"1.25rem" },
  tab: { flex:1, padding:"8px", background:"#f5f5f5", border:"1.5px solid #e0e0e0", borderRadius:"8px", cursor:"pointer", fontSize:"0.85rem", fontWeight:"600", color:"#888" },
  tabActive: { border:"1.5px solid #667eea", color:"#667eea", background:"#eef0ff" },
  errBox: { background:"#ffebee", color:"#b71c1c", borderRadius:"8px", padding:"0.75rem", fontSize:"0.85rem", marginBottom:"1rem" },
  form: { display:"flex", flexDirection:"column", gap:"10px" },
  input: { padding:"0.7rem 1rem", border:"1.5px solid #e0e0e0", borderRadius:"10px", fontSize:"0.95rem", outline:"none", width:"100%", boxSizing:"border-box" },
  btnPrimary: { padding:"0.9rem", background:"linear-gradient(135deg,#667eea,#764ba2)", color:"#fff", border:"none", borderRadius:"10px", fontWeight:"800", cursor:"pointer", fontSize:"1rem" },
  errorIcon: { fontSize:"3rem", textAlign:"center", marginBottom:"1rem" },
  title: { fontSize:"1.25rem", fontWeight:"700", color:"#1a1a2e", textAlign:"center", marginBottom:"0.5rem" },
  desc: { color:"#888", fontSize:"0.9rem", textAlign:"center", marginBottom:"1.5rem" },
  btn: { display:"block", textAlign:"center", padding:"0.75rem", background:"#667eea", color:"#fff", borderRadius:"10px", textDecoration:"none", fontWeight:"700" },
};
